import React from "react";
import { useHistory, useParams } from "react-router-dom";
import { ApiSchemaPatientTask, PagedRequestParam } from "../models/api";
import { AppUserRoles } from "./constants";
import { TypeTemplate } from "./templateType";
import { StorageAuth } from "../core/localStorage";
import { AppConfig } from "./appConfig";

const GoogleGeoCodeApiKey = process.env.REACT_APP_GOOGLE_GEOCODE_API || "";

export const AppRouteApi = {
  Root: AppConfig.RootApi,
  UserRoot: AppConfig.RootApiUser,
  AppointmentRoot: AppConfig.RootApiAppointment,
  SuperUserRoot: AppConfig.RootApiSuperUser,
  MediaRoot: AppConfig.RootApiMedia,
  ResourceRoot: AppConfig.RootApiResource,
  HouseKeepingRoot: AppConfig.RootApiSuperUser,
  Auth: {
    Root: () => `${AppConfig.RootApiAuth}/Auth`,
    Login: () => `${AppRouteApi.Auth.Root()}/Login`,
    Logout: () => `${AppRouteApi.Auth.Root()}/LogOut`,
    ForgotPassword: (email: string) =>
      `${AppRouteApi.Auth.Root()}/ForgotPassword?email=${email}`,
    ChangePasswordWithCode: () =>
      `${AppRouteApi.Auth.Root()}/ResetPasswordWithCode`,
    ChangePassword: () => `${AppRouteApi.Auth.Root()}/ChangePassword`,
    ActivateUser: () => `${AppRouteApi.Auth.Root()}/ActivateUser`,
  },
  Profile: {
    Caregiver: () => `${AppConfig.RootApiUser}/CareGiver/Profile`,
    Patient: () => `${AppConfig.RootApiUser}/Patient/Profile`,
    SuperUser: () => `${AppConfig.RootApiSuperUser}/SuperAdminUser/Profile`,
  },
  Dashboard: {
    Admin: () => `${AppConfig.RootApiSuperUser}/AdminDashboard`,
  },
  PatientTask: {
    Root: () => `${AppRouteApi.Root}/PatientTask`,
    Paginated: (pageData?: PagedRequestParam) => {
      const params = [];
      if (pageData?.searchKey) {
        params.push(`SearchKey=${pageData.searchKey}`);
      }
      if (pageData?.pageNumber) {
        params.push(`PageNumber=${pageData.pageNumber}`);
      }
      if (pageData?.pageSize) {
        params.push(`PageSize=${pageData.pageSize}`);
      }
      return `${AppRouteApi.PatientTask.Root()}/Paginated${
        params.length > 0 ? "?" + params.join("&") : ""
      }`;
    },
    ById: (id: string) => `${AppRouteApi.PatientTask.Root()}/${id}`,
    Delete: (id: string) => `${AppRouteApi.PatientTask.Root()}?id=${id}`,
    DeleteMultiple: () => `${AppRouteApi.PatientTask.Root()}/Multiple`,
    Search: (searchKey: string) =>
      `${AppRouteApi.PatientTask.Root()}/search?searchKey=${searchKey}`,
    Export: (type: "Csv" | "Excel") =>
      `${AppRouteApi.PatientTask.Root()}/Export${type}`,
    ExportAll: (type: "Csv" | "Excel") =>
      `${AppRouteApi.PatientTask.Root()}/Export${type}/All`,
  },
  InsuranceTypes: {
    Root: () => `${AppRouteApi.Root}/InsuranceTypes`,
    ById: (id: string) => `${AppRouteApi.InsuranceTypes.Root()}/${id}`,
    Delete: (id: string) => `${AppRouteApi.InsuranceTypes.Root()}?id=${id}`,
    ByCompanyId: (companyId: string) =>
      `${AppRouteApi.InsuranceTypes.Root()}?companyId=${companyId}`,
    Paginated: () => `${AppRouteApi.Root}/InsuranceTypes/GetAllPaginated`,
  },
  Patient: {
    Root: () => `${AppConfig.RootApiUser}/Patient`,
    DataExchange: () => `${AppRouteApi.Patient.Root()}/data-exchange`,
    All: () => `${AppConfig.RootApiUser}/Patient/all`,
    List: () => `${AppConfig.RootApiUser}/Patient/GetForDropDown`,
    ById: (id: string) => `${AppRouteApi.Patient.Root()}/${id}`,
    Delete: (ids: string[]) =>
      `${AppRouteApi.Patient.Root()}/?${ids
        .map((id) => "ids=" + id)
        .join("&")}`,
    Export: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Patient.Root()}/Export${type}`,
    ExportAll: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Patient.Root()}/Export${type}/All`,
    ActivateDeactivate: (id: string) =>
      `${AppRouteApi.Patient.Root()}/ActiveDeactive/${id}`,
    ChangeSignatureRequirement: (id: string) =>
      `${AppRouteApi.Patient.Root()}/ChangeSignatureRequirement/${id}`,
    ExpiredCount: () => `${AppRouteApi.Patient.Root()}/Expired/Count`,
  },
  Caregiver: {
    Root: () => `${AppConfig.RootApiUser}/CareGiver`,
    All: () => `${AppConfig.RootApiUser}/CareGiver/all`,
    List: () => `${AppConfig.RootApiUser}/Caregiver/GetForDropDown`,
    ById: (id: string) => `${AppRouteApi.Caregiver.Root()}/${id}`,
    Delete: (ids: string[]) =>
      `${AppRouteApi.Caregiver.Root()}/?${ids
        .map((id) => "ids=" + id)
        .join("&")}`,
    Export: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Caregiver.Root()}/Export${type}`,
    ExportAll: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Caregiver.Root()}/Export${type}/All`,
    ActivateDeactivate: (id: string) =>
      `${AppRouteApi.Caregiver.Root()}/ActiveDeactive/${id}`,
  },
  Company: {
    Root: () => `${AppConfig.RootApiSuperUser}/Company`,
    All: () => `${AppConfig.RootApiSuperUser}/Company/All`,
    AllDetails: () => `${AppRouteApi.Company.Root()}/AllDetails`,
    AllCompanies: () => `${AppRouteApi.Company.Root()}/all`,
    Register: () => `${AppRouteApi.Company.Root()}/Register`,
    ById: (id: string) => `${AppRouteApi.Company.Root()}/${id}`,
    Delete: () => `${AppRouteApi.Company.Root()}`,
    Export: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Company.Root()}/Export${type}`,
    ExportAll: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Company.Root()}/Export${type}/All`,
    RequestSubscription: () =>
      `${AppRouteApi.Company.Root()}/RequestSubscription`,
    GPSCheckInToggle: () => `${AppRouteApi.Company.Root()}/UpdateGpsStatus`,
    AutoClockCheckInToggle: () =>
      `${AppRouteApi.Company.Root()}/UpdateAutoClockInStatus`,
    AddEmailPassword: () => `${AppRouteApi.Company.Root()}/AddEmailPassword`,
    GetSenderEmail: () => `${AppRouteApi.Company.Root()}/GetSenderEmail`,
  },
  Users: {
    Root: () => `${AppConfig.RootApiSuperUser}/SuperAdminUser`,
    All: () => `${AppConfig.RootApiSuperUser}/SuperAdminUser/all`,
    ById: (id: string) => `${AppRouteApi.Users.Root()}/${id}`,
    Export: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Users.Root()}/Export${type}`,
    ExportAll: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Users.Root()}/Export${type}/All`,
    Profile: () => `${AppRouteApi.Users.Root()}/Profile`,
    Settings: () => `${AppRouteApi.Users.Root()}/AccountSettings`,
  },
  Appointment: {
    Root: () => `${AppConfig.RootApiAppointment}/Appointment`,
    All: () => `${AppConfig.RootApiAppointment}/Appointment/All`,
    Paginated: () =>
      `${AppConfig.RootApiAppointment}/Appointment/GetAllPaginated`,
    Filter: () => `${AppRouteApi.Appointment.Root()}/Filter`,
    ThisMonth: () => `${AppRouteApi.Appointment.Root()}/ThisMonth`,
    ByDate: () => `${AppRouteApi.Appointment.Root()}/ByDate`,
    ById: (id: string) =>
      `${AppRouteApi.Appointment.Root()}/GetAppointmentForUpdate/${id}`,
    CheckIn: () => `${AppRouteApi.Appointment.Root()}/CheckIn`,
    CheckInForce: () => `${AppRouteApi.Appointment.Root()}/v1/CheckInForce`,
    CheckOut: () => `${AppRouteApi.Appointment.Root()}/CheckOut`,
    CheckOutForce: () => `${AppRouteApi.Appointment.Root()}/v1/CheckOutForce`,
    Delete: (id: string) => `${AppRouteApi.Appointment.Root()}/?id=${id}`,
    Export: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Appointment.Root()}/Export${type}`,
    ExportAll: (type: "Csv" | "Excel") =>
      `${AppRouteApi.Appointment.Root()}/Export${type}/All`,
    AppointmentAssignments: () =>
      `${AppRouteApi.Appointment.Root()}/GetAppointmentAssignments`,
    ManageCaregiverAssignments: () =>
      `${AppRouteApi.Appointment.Root()}/ManageCaregiverAppointmentAssignment`,
    GetHospitalisationTypes: () =>
      `${AppRouteApi.Appointment.Root()}/GetHospitalisationTypes`,
    ByDateRange: () => `${AppRouteApi.Appointment.Root()}/ByDateRange`,
    ByDateRangeV2: () => `${AppRouteApi.Appointment.Root()}/v2/ByDateRange`,
    ChangeStatus: () => `${AppRouteApi.Appointment.Root()}/UpdateStatus`,
    GetReferrals: () => `${AppRouteApi.Appointment.Root()}/GetReferrals`,
    GetAppointmentCountWithPendingInvoice: () =>
      `${AppRouteApi.Appointment.Root()}/GetMissingInvoiceCount`,
    GetReportByStatus: () =>
      `${AppRouteApi.Appointment.Root()}/GetReportByStatus`,
    GetAppointmentIdByPatient: (patientSKey: string, appointmentId?: string) =>
      `${AppRouteApi.Appointment.Root()}/GetAppointmentIdsByPatient/${patientSKey}${
        appointmentId ? `?appointmentId=${appointmentId}` : ""
      }`,
    GetActivityByAppointmentIdAndDate: () =>
      `${AppRouteApi.Appointment.Root()}/GetActivityByAppointmentIdAndDate`,
  },
  AppointmentActivity: {
    Root: () => `${AppConfig.RootApiAppointment}/AppointmentActivity`,
    Caregiver: () => `${AppRouteApi.AppointmentActivity.Root()}/Caregiver`,
    Company: () => `${AppRouteApi.AppointmentActivity.Root()}/GetActivityLog`,
    Patient: () => `${AppRouteApi.AppointmentActivity.Root()}/Patient-all`,
  },
  TaskSettings: {
    Root: () => `${AppRouteApi.Root}/TaskSetting`,
    ById: (CompanyId: string) => `${AppRouteApi.Root}/TaskSetting/${CompanyId}`,
  },
  Media: {
    ByPath: (path: string) => `${AppConfig.RootApiMedia}/${path}`,
  },
  Language: {
    Root: () => `${AppConfig.RootApiResource}/StaticResources/languages.json`,
  },
  Finance: {
    Root: () => `${AppRouteApi.Root}/Finance`,
    Patient: () => `${AppRouteApi.Finance.Root()}/Patient`,
    PatientV2: () => `${AppRouteApi.Finance.Root()}/v2/Patient`,
    PatientDetails: () => `${AppRouteApi.Finance.Patient()}/Details`,
    AllPatients: () => `${AppRouteApi.Finance.Patient()}-all/Details`,
    Caregiver: () => `${AppRouteApi.Finance.Root()}/Caregiver`,
    CaregiverV2: () => `${AppRouteApi.Finance.Root()}/v2/Caregiver`,
    AllCaregivers: () => `${AppRouteApi.Finance.Caregiver()}-all`,
    MarkAsPaidPatient: () => `${AppRouteApi.Finance.Root()}/MarkAsPaid/Patient`,
    MarkAsPaidCaregiver: () =>
      `${AppRouteApi.Finance.Root()}/MarkAsPaid/Caregiver`,
    EmailCaseManager: () => `${AppRouteApi.Finance.Root()}/EmailCaseManager`,
    SendMail: () => `${AppRouteApi.Finance.Root()}/SendEmail`,
    GenerateInvoice: () => `${AppRouteApi.Finance.Root()}/v2/InvoiceTest`,
    GenerateInvoiceV2: (invokeType?: string, selectedDate?: Date) =>
      `${AppRouteApi.Finance.Root()}/v3/InvoiceTest?invokeType=${invokeType}&selectedDate=${selectedDate?.toISOString()}`,
    GenerateInvoiceV5: (invokeType?: string, selectedDate?: Date) =>
      `${AppRouteApi.Finance.Root()}/v5/InvoiceTest?invokeType=${invokeType}&selectedDate=${selectedDate?.toISOString()}`,
    InvoiceByCaregiverSkey: (
      caregiverSkey: string,
      selectedDate: Date,
      invokeType: string
    ) =>
      `${AppRouteApi.Finance.Root()}/InvoiceByCaregiverSkey?caregiverSkey=${caregiverSkey}&selectedDate=${selectedDate.toISOString()}&invokeType=${invokeType}`,
  },
  PaymentCard: {
    Root: () => `${AppConfig.RootApiUser}/PaymentCard`,
    PaymentCardDetails: (companyId: string) =>
      `${AppRouteApi.PaymentCard.Root()}/GetPaymentCardDetails/${companyId}`,
    UpdatePaymentCard: () =>
      `${AppRouteApi.PaymentCard.Root()}/UpdatePaymentCard`,
  },
  AdvancedInvoice: {
    Root: () => `${AppConfig.RootApiUser}/AdvancedInvoice`,
    Invoices: () =>
      `${AppRouteApi.AdvancedInvoice.Root()}/GetAdvancedBillingInvoices`,
  },
  Invoice: {
    Root: () => `${AppRouteApi.Root}/Invoice`,
    V2: (showMissing = false) =>
      `${AppRouteApi.Invoice.Root()}/v2?DisplayMissing=${showMissing}`,
    All: () => `${AppRouteApi.Invoice.Root()}/all`,
    InvoiceTasks: (invoiceId?: string) => {
      const invoiceIdRoute = invoiceId ? `/${invoiceId}` : "";
      return `${AppRouteApi.Invoice.Root()}/GetTasks${invoiceIdRoute}`;
    },
    Submit: () => `${AppRouteApi.Invoice.Root()}/Submit`,
    Excel: (invoiceId: string) =>
      `${AppRouteApi.Invoice.Root()}/Excel?invoiceId=${invoiceId}`,
    Pdf: (invoiceId: string) =>
      `${AppRouteApi.Invoice.Root()}/Pdf?invoiceId=${invoiceId}`,
    TimeAndTaskExcel: (appointmentId: string) =>
      `${AppRouteApi.Invoice.Root()}/TimeAndTaskExcel?invoiceId=${appointmentId}`,
    SendMail: () => `${AppRouteApi.Invoice.Root()}/SendInvoiceEmail`,
    ExcelFinance: () => `${AppRouteApi.Invoice.Root()}/ExcelFinance`,
    ExcelPreview: () => `${AppRouteApi.Invoice.Root()}/ExcelPreview`,
    FinancePreview: () => `${AppRouteApi.Invoice.Root()}/FinancePreview`,
    DeleteById: (id: string) => `${AppRouteApi.Invoice.Root()}/v1/${id}`,
    AppointmentInvoiceRecord: (id: string) =>
      `${AppRouteApi.Invoice.Root()}/AppointmentInvoicesByAppointmentSkey/${id}`,
  },
  Insurance: {
    Root: () => `${AppConfig.RootApiUser}/Insurance`,
    ById: (id: string) => `${AppRouteApi.Insurance.Root()}/${id}`,
    Delete: (ids: string[]) =>
      `${AppRouteApi.Insurance.Root()}/?${ids
        .map((id) => "ids=" + id)
        .join("&")}`,
  },
  Reporting: {
    PatientCountByInsuranceType: () =>
      `${AppConfig.RootApiSuperUser}/AdminDashboard/PatientCountByInsuranceType`,
    PaginatedPatientCountByInsuranceType: () =>
      `${AppConfig.RootApiSuperUser}/AdminDashboard/PaginatedPatientCountByInsuranceType`,
    CaregiverCountByTask: () =>
      `${AppConfig.RootApiSuperUser}/AdminDashboard/PaginatedCaregiverCountByTask`,
    CaregiversReport: () =>
      `${AppConfig.RootApiSuperUser}/AdminDashboard/PaginatedCaregiversReport`,
  },
  Download: {
    Template: (type: TypeTemplate) =>
      `${AppConfig.RootApiUser}/DataImport/GetTemplate/${StorageAuth.CompanyId}/${type}`,
  },
  Import: {
    Root: () => `${AppConfig.RootApiUser}/DataImport`,
    Patient: () => `${AppRouteApi.Import.Root()}/ImportPatients/`,
    Caregiver: () => `${AppRouteApi.Import.Root()}/ImportCaregivers/`,
    Status: (importKey: string) =>
      `${AppRouteApi.Import.Root()}/GetImportStatus/${importKey}`,
  },
  ThirdParty: {
    GoogleMap: () => `https://maps.googleapis.com/maps/api`,
    GeoCode: (address: string) =>
      `${AppRouteApi.ThirdParty.GoogleMap()}/geocode/json?address=${encodeURIComponent(
        address
      )}&key=${GoogleGeoCodeApiKey}`,
  },
  DataExchangeSetting: {
    Root: () => `${AppRouteApi.SuperUserRoot}/DataExchangeSetting`,
    v1: () => `${AppRouteApi.DataExchangeSetting.Root()}/v1`,
  },
  HouseKeeping: {
    Root: () => `${AppConfig.RootApiSuperUser}/HouseKeeping`,
    GenerateToken: () => `${AppRouteApi.HouseKeeping.Root()}/generateToken`,
    UpdatePatientHHAIdByPatientIdAsync: (patientId: string) =>
      `${AppRouteApi.HouseKeeping.Root()}/v1/UpdatePatientHHAIdByPatientId?patientId=${patientId}`,
    GetToken: () => `${AppRouteApi.HouseKeeping.Root()}/v1/getToken`,
    CaregiverById: (id: string) =>
      `${AppRouteApi.HouseKeeping.Root()}/CaregiverById?caregiverId=${id}`,
    VisitByDateRange: () =>
      `${AppRouteApi.HouseKeeping.Root()}/VisitByDateRange`,
    PatientById: (id: string) =>
      `${AppConfig.RootApiSuperUser}/HouseKeeping/PatientById?patientId=${id}`,
  },
  AppointmentCheck: {
    Root: () => `${AppConfig.RootApiAppointment}/AppointmentCheck`,
    GetByDateRange: () =>
      `${AppRouteApi.AppointmentCheck.Root()}/v2/GetByDateRange`,
    GetDetailedActivity: () =>
      `${AppRouteApi.AppointmentCheck.Root()}/v1/GetDetailedActivity`,
  },
};

export const AppRouteUi = {
  Root: "",
  Login: {
    Root: () => `${AppRouteUi.Root}/login`,
    ForgotPassword: () => `${AppRouteUi.Login.Root()}/forgot-password`,
    ResetPassword: () => `${AppRouteUi.Login.Root()}/Reset-password`,
    RegisterCompany: () => `${AppRouteUi.Login.Root()}/register-company`,
  },
  ManageTaskList: {
    Root: () => `${AppRouteUi.Root}/task-list`,
    Update: {
      Root: () => `${AppRouteUi.ManageTaskList.Root()}/update/:taskId`,
      ByTaskId: (taskId: string) =>
        `${AppRouteUi.ManageTaskList.Root()}/update/${taskId}`,
      useRedirectUrl: (): ((taskData: ApiSchemaPatientTask) => void) => {
        const routeHistory = useHistory();
        return React.useCallback((taskData: ApiSchemaPatientTask) => {
          if (taskData.sKey) {
            routeHistory.push(
              AppRouteUi.ManageTaskList.Update.ByTaskId(taskData.sKey),
              taskData
            );
          }
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);
      },
      useParam: (): string | undefined => {
        const param = useParams<{ taskId: string }>();
        return param.taskId;
      },
    },
    Add: () => `${AppRouteUi.ManageTaskList.Root()}/Add`,
  },
  Dashboard: {
    Root: () => `${AppRouteUi.Root}/dashboard`,
  },
  Report: {
    Root: () => `${AppRouteUi.Root}/Report`,
  },
  Caregiver: {
    Root: () => `${AppRouteUi.Root}/caregiver`,
    List: () => `${AppRouteUi.Caregiver.Root()}/list`,
    Add: () => `${AppRouteUi.Caregiver.Root()}/add`,
    ById: () => `${AppRouteUi.Caregiver.Root()}/:caregiverId`,
    ByIdV2: (id: string) => `${AppRouteUi.Caregiver.Root()}/${id}`,
    Update: () => `${AppRouteUi.Caregiver.Root()}/update/:caregiverId`,
    UpdateById: (id: string) => `${AppRouteUi.Caregiver.Root()}/update/${id}`,
    AddPatient: () => `${AppRouteUi.Caregiver.Root()}/addPatient/:caregiverId`,
    AddPatientById: (id: string) =>
      `${AppRouteUi.Caregiver.Root()}/addPatient/${id}`,
  },
  Patient: {
    Root: () => `${AppRouteUi.Root}/patient`,
    List: () => `${AppRouteUi.Patient.Root()}/list`,
    Add: () => `${AppRouteUi.Patient.Root()}/add`,
    ById: () => `${AppRouteUi.Patient.Root()}/:patientId`,
    ByIdV2: (id: string) => `${AppRouteUi.Patient.Root()}/${id}`,
    Update: () => `${AppRouteUi.Patient.Root()}/update/:patientId`,
    UpdateById: (id: string) => `${AppRouteUi.Patient.Root()}/update/${id}`,
  },
  Appointment: {
    Root: () => `${AppRouteUi.Root}/appointment`,
    List: () => `${AppRouteUi.Appointment.Root()}/list`,
    Add: () => `${AppRouteUi.Appointment.Root()}/add`,
    Update: () => `${AppRouteUi.Appointment.Root()}/update/:appointmentId`,
    UpdateById: (id: string) => `${AppRouteUi.Appointment.Root()}/update/${id}`,
    BackdatedInvoiceGeneration: (id: string) =>
      `${AppRouteUi.Appointment.Root()}/backdated-invoice/${id}`,
  },
  Company: {
    Root: () => `${AppRouteUi.Root}/company`,
    List: () => `${AppRouteUi.Company.Root()}/list`,
    Add: () => `${AppRouteUi.Company.Root()}/add`,
    ById: () => `${AppRouteUi.Company.Root()}/:companyId`,
    Update: () => `${AppRouteUi.Company.Root()}/update/:companyId`,
    UpdateById: (id: string) => `${AppRouteUi.Company.Root()}/update/${id}`,
  },
  Finance: {
    Root: () => `${AppRouteUi.Root}/finance`,
    Patient: () => `${AppRouteUi.Finance.Root()}/patient`,
    Caregiver: () => `${AppRouteUi.Finance.Root()}/caregiver`,
  },
  Invoice: {
    Root: () => `${AppRouteUi.Root}/invoice`,
    List: () => `${AppRouteUi.Invoice.Root()}/list`,
    Edit: (id?: string) =>
      `${AppRouteUi.Invoice.Root()}/edit/${!!id ? id : ":invoiceId"}`,
    ById: (id?: string) =>
      `${AppRouteUi.Invoice.Root()}/${!!id ? id : ":invoiceId"}`,
  },
  Insurance: {
    Root: () => `${AppRouteUi.Root}/insurance`,
    List: () => `${AppRouteUi.Insurance.Root()}/list`,
    Add: () => `${AppRouteUi.Insurance.Root()}/add`,
    ById: () => `${AppRouteUi.Insurance.Root()}/:insuranceId`,
    Update: () => `${AppRouteUi.Insurance.Root()}/update/:insuranceId`,
    UpdateById: (id: string) => `${AppRouteUi.Insurance.Root()}/update/${id}`,
  },
  Settings: {
    Root: () => `${AppRouteUi.Root}/settings`,
  },
  Users: {
    Root: () => `${AppRouteUi.Root}/users`,
    List: () => `${AppRouteUi.Users.Root()}/list`,
    Add: () => `${AppRouteUi.Users.Root()}/add`,
    Update: () => `${AppRouteUi.Users.Root()}/update/:userId`,
    ById: () => `${AppRouteUi.Users.Root()}/:userId`,
    UpdateById: (userId: string) =>
      `${AppRouteUi.Users.Root()}/update/${userId}`,
  },
  Checkout: {
    Root: () => `${AppRouteUi.Root}/checkout`,
  },
  PrivacyPolicy: {
    Root: () => `${AppRouteUi.Root}/privacy-policy`,
  },
  NotFound: {
    Root: () => `${AppRouteUi.Root}/404`,
  },
  Import: {
    Root: () => `${AppRouteUi.Root}/import`,
  },
  Billing: {
    Root: () => `${AppRouteUi.Root}/billing`,
    PaymentMethod: () => `${AppRouteUi.Billing.Root()}/payment-method`,
    Invoice: () => `${AppRouteUi.Billing.Root()}/invoice`,
    ViewInvoice: (id: string) => `${AppRouteUi.Billing.Invoice()}/${id}`,
  },
  DataExchange: {
    Root: () => `${AppRouteUi.Root}/data-exchange`,
  },
  HHAConfig: {
    Root: () => `${AppRouteUi.Root}/hha-config`,
    Edit: (id: string) => `${AppRouteUi.HHAConfig.Root()}/edit/${id}`,
    Add: () => `${AppRouteUi.HHAConfig.Root()}/add`,
  },
  GetRouteByRole: (role: AppUserRoles): string =>
    role === AppUserRoles.SuperAdmin
      ? AppRouteUi.Company.Root()
      : role === AppUserRoles.Admin
      ? AppRouteUi.Dashboard.Root()
      : AppRouteUi.NotFound.Root(),
};
